import { wcagContrast } from "culori";
import { fromLch, type Hex, toLch } from "./oklch";

const ratios = new Map<string, number>();

/** WCAG 2.x contrast ratio, 1..21 (memoized: the UI asks for hundreds per edit). */
export function contrastRatio(fg: Hex, bg: Hex): number {
  const key = fg + bg;
  let r = ratios.get(key);
  if (r === undefined) {
    if (ratios.size > 8192) ratios.clear();
    r = wcagContrast(fg, bg);
    ratios.set(key, r);
  }
  return r;
}

/** Absolute OKLCH lightness difference in percentage points (0..100). */
export function lightnessDelta(fg: Hex, bg: Hex): number {
  return Math.abs(toLch(fg).l - toLch(bg).l) * 100;
}

export type ContrastLevel = "text" | "secondary" | "decorative";

export interface ContrastThresholds {
  text: number;
  secondary: number;
  decorative: number;
}

/** WCAG AA body text, AA large text / UI components, and a floor for dim decoration. */
export const DEFAULT_THRESHOLDS: ContrastThresholds = { text: 4.5, secondary: 3, decorative: 1.5 };

export type ContrastGrade = "pass" | "close" | "fail";

export function grade(ratio: number, min: number): ContrastGrade {
  if (ratio >= min) return "pass";
  if (ratio >= min * 0.85) return "close";
  return "fail";
}

/**
 * Moves `fg` away from `bg` in OKLCH lightness (hue and chroma kept) until the WCAG ratio
 * reaches `min`. Returns the closest color that passes, or the best effort if none does.
 */
export function fixContrast(fg: Hex, bg: Hex, min: number): Hex {
  if (contrastRatio(fg, bg) >= min) return fg;
  const start = toLch(fg);
  const bgL = toLch(bg).l;
  const directions = start.l >= bgL ? [1, -1] : [-1, 1];
  for (const dir of directions) {
    for (let step = 1; step <= 100; step++) {
      const l = start.l + dir * step * 0.01;
      if (l < 0 || l > 1) break;
      const candidate = fromLch({ ...start, l });
      if (contrastRatio(candidate, bg) >= min) return candidate;
    }
  }
  return start.l >= bgL ? "#ffffff" : "#000000";
}
